import { z } from "zod";
import { createCustomSettings } from "@rootreeweb/zod-to-elements";
import { dimensionsSchema } from "./dimensions.js";
import { pouchTypeSchema } from "./pouchType.js";
import { sealsSchema } from "./seals.js";
export const baseDielineSchema = z.object({
    pouchType: pouchTypeSchema,
    dimensions: dimensionsSchema.describe("Dimensions (mm)"),
    cap: z
        .string()
        .optional()
        .describe(createCustomSettings({
        label: "Cap",
        dependsOn: {
            field: "requestDetails.pouchType",
            // value: ["Top Spout", "Corner Spout"],
            value: [],
        },
    })),
    openSide: z
        .union([
        z.literal("open-top").describe("Top Open"),
        z.literal("open-bottom").describe("Bottom Open"),
    ])
        .optional()
        .describe(createCustomSettings({
        label: "Seal Type",
        dependsOn: {
            field: "requestDetails.pouchType",
            value: ["2-Side Seal", "3-Side Seal"],
        },
    })),
    spoutAngle: z
        .union([
        z
            .literal("45deg-corner-spout-lws-f")
            .describe("45° Corner Spout - LWS-F (Cap Off - Top Sealed)"),
        z
            .literal("45deg-corner-spout-lws-a")
            .describe("45° Corner Spout - LWS-A (Cap On - Top Open)"),
        z
            .literal("30deg-60deg-corner-spout-lws-F")
            .describe("30°/60° Corner Spout - LWS-F (Cap Off - Top Sealed)"),
        z
            .literal("30deg-60deg-corner-spout-lws-A")
            .describe("30°/60° Corner Spout - LWS-A (Cap On - Top Opened)"),
    ])
        .optional()
        .describe(createCustomSettings({
        label: "Spout Type",
        dependsOn: {
            field: "requestDetails.pouchType",
            value: ["Corner Spout"],
        },
    })),
    sealType: sealsSchema,
    gussetColour: z
        .union([z.literal("Metalized"), z.literal("Clear"), z.literal("White")])
        .optional()
        .describe(createCustomSettings({
        label: "Gusset Colour",
        dependsOn: {
            field: "requestDetails.sealType",
            value: ["inserted-doy", "inserted-k-seal-with-skirt"],
        },
    })),
    punchHole: z
        .object({
        type: z
            .union([
            z.literal("no-punch-hole").describe(createCustomSettings({
                label: "No Punch Hole",
                isNoOption: true,
            })),
            z.literal("round-hang-hole").describe("Round Hang Hole"),
            z.literal("euro-slot").describe("Euro Slot"),
            z.literal("die-cut-handle").describe("Die Cut Handle"),
            z.literal("pin-hole").describe("Pin Hole"),
        ])
            .describe("Type"),
        position: z
            .union([
            z.literal("standard").describe("Standard"),
            z.literal("Custom"),
            z.string(),
        ])
            .optional()
            .describe(createCustomSettings({
            label: "Position",
            dependsOn: "requestDetails.punchHole.type",
        })),
    })
        .describe("Punch Hole"),
    tearNotches: z
        .union([
        z.literal("no-tear-notches").describe(createCustomSettings({
            label: "No Tear Notches",
            isNoOption: true,
        })),
        z.literal("9mm").describe(createCustomSettings({
            hide: true,
        })),
        z.literal("10mm").describe(createCustomSettings({
            hide: true,
        })),
        z.literal("15mm").describe("Standard 15mm"),
        z.literal("16mm").describe(createCustomSettings({
            hide: true,
        })),
        z.literal("19mm").describe(createCustomSettings({
            hide: true,
        })),
        z.literal("20mm").describe(createCustomSettings({
            hide: true,
        })),
        z.literal("21mm").describe(createCustomSettings({
            hide: true,
        })),
        z.literal("22mm").describe("Lowered 22mm"),
        z.literal("25mm").describe(createCustomSettings({
            hide: true,
        })),
        z.literal("29mm").describe(createCustomSettings({
            hide: true,
        })),
        z.literal("30mm").describe(createCustomSettings({
            hide: true,
        })),
        z.string(),
        z.literal("Custom"),
    ])
        .describe("Tear Notches"),
    zipper: z
        .object({
        type: z
            .union([
            z
                .literal("no-zipper")
                .describe(createCustomSettings({ label: "No Zipper", isNoOption: true })),
            z.literal("standard-zipper").describe("Standard Zipper"),
            z.literal("powder-zipper").describe("Powder Zipper"),
            z.literal("compostable-zipper").describe("Compostable Zipper"),
            z
                .literal("child-resistant-zipper")
                .describe("Child Resistant Zipper"),
            z
                .literal("child-guard-slider-zipper")
                .describe("Child Guard Slider Zipper"),
            z.literal("ergo-slider-zipper").describe("Ergo Slider Zipper"),
            z
                .literal("zip-pack-velcro-style-zipper")
                .describe("Zip Pack Velcro Style Zipper"),
        ])
            .describe("Type"),
        position: z
            .union([
            z.literal("standard-25mm").describe("Standard 25mm"),
            z.literal("lowered-30mm").describe("Lowered 30mm"),
            z.string(),
            z.literal("Custom"),
        ])
            .optional()
            .describe(createCustomSettings({
            label: "Position",
            dependsOn: "requestDetails.zipper.type",
        })),
    })
        .describe("Zipper"),
    roundedCorners: z.boolean().default(false).describe("Rounded Corners"),
    valve: z
        .object({
        type: z
            .union([
            z.literal("No").describe(createCustomSettings({
                label: "No Valve",
                isNoOption: true,
            })),
            z.literal("Yes - Back Inline"),
            z.literal("Yes - Back Offline"),
            z.literal("Yes - Front Offline"),
            z.literal("Yes").describe(createCustomSettings({
                hide: true,
            })),
        ])
            .describe("Type"),
        position: z
            .union([
            z.literal("standard").describe("Standard"),
            z.string(),
            z.literal("Custom"),
        ])
            .optional()
            .describe(createCustomSettings({
            label: "Position",
            dependsOn: "requestDetails.valve.type",
        })),
    })
        .describe("Valve"),
    tinTie: z
        .object({
        on: z.boolean().default(false).describe("On"),
        side: z
            .union([z.literal("Front"), z.literal("Back")])
            .optional()
            .describe(createCustomSettings({
            label: "Tin Tie Side",
            dependsOn: "requestDetails.tinTie.on",
        })),
        position: z
            .union([
            z.literal("standard").describe("Standard"),
            z.string(),
            z.literal("Custom"),
        ])
            .optional()
            .describe(createCustomSettings({
            label: "Tin Tie Position",
            dependsOn: "requestDetails.tinTie.on",
        })),
    })
        .describe("Tin Tie"),
    material: z
        .union([z.literal("Conventional/Recyclable"), z.literal("Compostable")])
        .describe("Material")
        .optional(),
    converting: z
        .union([z.literal("1-Up"), z.literal("2-Up")])
        .optional()
        .describe(createCustomSettings({
        label: "Converting",
        hide: true,
    })),
});
export const dielineInfoSchema = baseDielineSchema.extend({
    file: z
        .string()
        .min(1)
        .describe(createCustomSettings({ hide: true })),
    name: z
        .string()
        .min(1)
        .describe(createCustomSettings({ hide: true })),
    size: z.string().describe(createCustomSettings({ label: "Size Label" })),
    fileSize: z
        .number()
        .optional()
        .describe(createCustomSettings({ hide: true })),
    hash: z
        .string()
        .optional()
        .describe(createCustomSettings({ hide: true })),
    eyeMarks: z
        .union([z.literal("On"), z.literal("Off")])
        .describe(createCustomSettings({ label: "Eye Marks" })),
    _id: z
        .string()
        .optional()
        .describe(createCustomSettings({ hide: true })),
    dropboxID: z.string().optional(),
    favourite: z.boolean().default(false).optional(),
    dielineURL: z.string().url().optional(),
});
//# sourceMappingURL=dieline.js.map